import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import EMPButton from "../shared/components/EMPButton/EMPButton";
import EMPOrderTable from "../shared/components/EMPOrderTable/EMPOrderTable";
import * as OrdersPageAnalytics from "../shared/services/analytics/OrdersPageAnalytics";
import { UserContext } from "../shared/context/userContext";
import { isCanadianPreorderUser } from "../utils/canadianPreorderExperience";

const MessageDisplay = ({ message, actionButton, canadianPreorder }) => (
  <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
    <Table
      sx={{ minWidth: canadianPreorder ? undefined : 650 }}
      aria-label="orders table"
    >
      <TableBody>
        <TableRow>
          <TableCell
            colSpan={7}
            sx={{
              height: "calc(100vh - 400px)",
              border: "none",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{ height: "100%", gap: 2 }}
            >
              <Typography variant="body1" textAlign="center">
                {message}
              </Typography>
              {actionButton}
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

MessageDisplay.propTypes = {
  actionButton: PropTypes.node,
  canadianPreorder: PropTypes.bool,
  message: PropTypes.string.isRequired,
};

MessageDisplay.defaultProps = {
  actionButton: null,
  canadianPreorder: false,
};

const OrdersTable = ({ orders, isError, refetch }) => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation("emp");
  const canadianPreorder = isCanadianPreorderUser(user);

  const handleRefresh = () => {
    OrdersPageAnalytics.trackRefreshPage(user);
    refetch();
  };

  if (isError) {
    return (
      <MessageDisplay
        canadianPreorder={canadianPreorder}
        message={
          canadianPreorder
            ? t("CanadianPreorderOrders.loadError", {
                defaultValue: "There has been an error. Please try again.",
              })
            : "There has been an error. Please try again."
        }
        actionButton={
          <EMPButton
            onClick={handleRefresh}
            variant="text"
            testId="retry-button"
          >
            {canadianPreorder
              ? t("CanadianPreorderOrders.retry", {
                  defaultValue: "Try Again",
                })
              : "Try Again"}
          </EMPButton>
        }
      />
    );
  }

  if (orders.length === 0) {
    return (
      <MessageDisplay
        canadianPreorder={canadianPreorder}
        message={
          canadianPreorder
            ? t("CanadianPreorderOrders.noOrders", {
                defaultValue: "No orders found.",
              })
            : "No orders found."
        }
      />
    );
  }

  return (
    <EMPOrderTable
      rows={orders}
      canadianPreorder={canadianPreorder}
      fixedLayout={user?.brand === "L"}
    />
  );
};

OrdersTable.propTypes = {
  isError: PropTypes.bool,
  orders: PropTypes.arrayOf(PropTypes.object),
  refetch: PropTypes.func.isRequired,
};

OrdersTable.defaultProps = {
  isError: false,
  orders: [],
};

export default OrdersTable;
